import type {PerkIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import Input from '@cohort/merchants/components/form/input/Input';
import UrlInput from '@cohort/merchants/components/form/input/UrlInput';
import {RadioCards} from '@cohort/merchants/components/form/RadioCards';
import {CsvUpload} from '@cohort/merchants/components/form/v2/CsvUpload';
import HighlightText from '@cohort/merchants/components/HighlightText';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import {parseCsvFile} from '@cohort/merchants/lib/form/utils';
import type {PerkFormValues} from '@cohort/merchants/pages/perks/perk/edit/utils';
import {CohortPromoCodeSchema} from '@cohort/shared/apps/cohort/perks/promoCode';
import {asArray} from '@cohort/shared/schema/common';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isDefined} from 'remeda';

type PromoCodesPreviewData = {
  codes: Array<string>;
  total: number;
};

const UploadedPromoCodesPreview: React.FC<PromoCodesPreviewData> = ({codes, total}) => {
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promo-code.configComponent',
  });

  if (total === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 bg-slate-50 p-4 pt-0 text-center text-xs text-slate-400">
      <p>{t('labelTotalCodes', {count: total})}</p>
      {codes.join(', ')}
      {total > 3 && <div className="text-xs">...</div>}
    </div>
  );
};

const DuplicatePromoCodesPreview: React.FC<PromoCodesPreviewData> = ({codes, total}) => {
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promo-code.configComponent',
  });

  if (total === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 bg-slate-50 p-4 pt-0 text-center text-xs text-red-400">
      <p>{t('labelDuplicateCodes', {count: total})}</p>
      {codes.join(', ')}
      {total > 3 && <div className="text-xs">...</div>}
    </div>
  );
};

const InvalidPromoCodesPreview: React.FC<{codes: Array<string>}> = ({codes}) => {
  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promo-code.configComponent',
  });

  if (codes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-2 bg-slate-50 p-4 pt-0 text-center text-xs text-red-400">
      <p>{t('labelInvalidCodes', {count: codes.length})}</p>
      {codes.join(', ')}
      {codes.length > 3 && <div className="text-xs">...</div>}
    </div>
  );
};

const CohortPromoCodePerkIntegrationConfigComponent: React.FC<
  PerkIntegrationConfigComponentProps
> = () => {
  const [csvUploadError, setCsvUploadError] = useState<string | undefined>(undefined);
  const [invalidCodes, setInvalidCodes] = useState<string[]>([]);
  const [duplicateCodesPreview, setDuplicateCodesPreview] = useState<PromoCodesPreviewData>({
    codes: [],
    total: 0,
  });
  const [uploadedPromoCodesPreview, setUploadedPromoCodesPreview] = useState<PromoCodesPreviewData>(
    {
      codes: [],
      total: 0,
    }
  );

  const {t} = useTranslation('app-cohort', {
    keyPrefix: 'perkIntegrations.promo-code.configComponent',
  });

  const {control, register, setValue, watch, setError, clearErrors} =
    useCohortForm<PerkFormValues>();
  const [type, config, currentAvailablePromoCodes, status] = watch([
    'integration.config.type',
    'integration.config',
    'integration.config.available',
    'status',
  ]);

  useEffect(() => {
    if (!config.type) {
      setValue('integration.config.type', 'universal');
      return;
    }
    if (
      config.type === 'unique' &&
      (!Array.isArray(config.available) || !Array.isArray(config.used))
    ) {
      setValue('integration.config.available', config.available ?? []);
      setValue('integration.config.used', config.used ?? []);
    }
  }, [config.type, config.available, config.used, setValue]);

  const validateAndRemoveDuplicates = (
    uploadedPromoCodes: Record<string, string | undefined>[]
  ): Array<string> => {
    const currentPromoCodesSet = new Set<string>();
    const duplicates: string[] = [];

    // Pre-populate set with existing codes
    asArray(config.available).forEach((code: string) => currentPromoCodesSet.add(code));
    config.used.forEach((code: string) => currentPromoCodesSet.add(code));

    const invalidCodes: Array<string> = [];

    // Single pass through uploaded codes - filter invalid and duplicates simultaneously
    const validCodes = uploadedPromoCodes.reduce((validCodes: string[], row) => {
      const code = Object.values(row)[0];
      if (!code) return validCodes;

      if (!CohortPromoCodeSchema.safeParse(code).success) {
        invalidCodes.push(code);
        return validCodes;
      }

      if (!currentPromoCodesSet.has(code)) {
        currentPromoCodesSet.add(code);
        validCodes.push(code);
      } else if (duplicates.length < 3) {
        duplicates.push(code);
      }
      return validCodes;
    }, []);

    setInvalidCodes(invalidCodes);

    setDuplicateCodesPreview({
      codes: duplicates,
      total: uploadedPromoCodes.length - validCodes.length,
    });

    return validCodes;
  };

  const computeLabelUnique = (): string => {
    const baseLabel = t('labelUnique');
    if (
      type !== 'unique' ||
      !currentAvailablePromoCodes ||
      currentAvailablePromoCodes.length === 0
    ) {
      return baseLabel;
    }

    return `${baseLabel} (${t('labelTotal', {count: currentAvailablePromoCodes.length})})`;
  };

  return (
    <div className="space-y-6">
      <RadioCards
        name="integration.config.type"
        // @Devs - Prevent the type from being changed if the perk is published.
        disabled={status === 'live'}
        direction="row"
        label={t('labelType')}
        register={register}
        control={control}
        onChange={() => {
          if (status === 'live') {
            return;
          }
          if (type === 'universal') {
            setValue('integration.config.available', []);
            setValue('integration.config.used', []);
            setUploadedPromoCodesPreview({codes: [], total: 0});
            setDuplicateCodesPreview({codes: [], total: 0});
            setCsvUploadError(undefined);
          }
        }}
        options={[
          {
            label: t('labelUniversal'),
            value: 'universal',
          },
          {
            label: computeLabelUnique(),
            value: 'unique',
          },
        ]}
      />
      <div className="flex flex-col gap-y-2">
        {type === 'unique' ? (
          <div>
            <CsvUpload
              retryIfUndetectableDelimiter
              customErrorMessage={csvUploadError}
              onParse={(lines: Array<Array<string>>) => {
                const {items: promoCodes} = parseCsvFile({lines, noHeaders: true});
                const promoCodesToAdd = validateAndRemoveDuplicates(promoCodes);
                if (promoCodesToAdd.length === 0) {
                  setCsvUploadError(t('labelNoNewPromoCodesWarning'));
                  return;
                }
                setCsvUploadError(undefined);
                setUploadedPromoCodesPreview({
                  codes: promoCodesToAdd.slice(0, 3),
                  total: promoCodesToAdd.length,
                });
                setValue(
                  'integration.config.available',
                  [...currentAvailablePromoCodes, ...promoCodesToAdd],
                  {shouldDirty: true}
                );
              }}
              fileSizeLimitInMb={10}
              onReset={() => {
                setUploadedPromoCodesPreview({codes: [], total: 0});
                setDuplicateCodesPreview({codes: [], total: 0});
                setInvalidCodes([]);
                setCsvUploadError(undefined);
              }}
            />
            {!isDefined(csvUploadError) && (
              <>
                <UploadedPromoCodesPreview
                  codes={uploadedPromoCodesPreview.codes}
                  total={uploadedPromoCodesPreview.total}
                />
                <DuplicatePromoCodesPreview
                  codes={duplicateCodesPreview.codes}
                  total={duplicateCodesPreview.total}
                />
                <InvalidPromoCodesPreview codes={invalidCodes} />
              </>
            )}
          </div>
        ) : (
          <Input
            type="text"
            label={t('labelInputUniversalPromoCode')}
            name="integration.config.code"
            placeholder={t('placeholderInputUniversalPromoCode')}
            register={register}
            control={control}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!CohortPromoCodeSchema.safeParse(e.target.value).success) {
                setError('integration.config.code', {
                  message: t('labelInvalidPromoCodeWarning'),
                });
              } else {
                clearErrors('integration.config.code');
              }
            }}
          />
        )}
        {type === 'unique' && <HighlightText type="warning" text={t('informationText')} />}
      </div>

      <div className="flex flex-col gap-y-2">
        <UrlInput
          label={t('labelRedirectionUrl')}
          name="integration.config.redirectionUrl"
          register={register}
          control={control}
        />
        <HighlightText type="info" text={t('infoRedirectionUrl')} />
      </div>
    </div>
  );
};

export default CohortPromoCodePerkIntegrationConfigComponent;
